import React, { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { rootFixedColor } from "styles/theme";
import { BsEye, BsEyeSlash } from "react-icons/bs";
import styled from 'styled-components';
import { adminLogin, agentLogin, encryptSHA256, generateSecureRandomString } from "api/api";
import { useNavigate } from 'react-router-dom';
import { authEncryptAes256 } from 'api/deployignore';
import { useRecoilState } from 'recoil';
import { isDarkAtom } from 'styles/atoms';

const MainContainer = styled.section`
  color: #f2f3f5;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  .title {
    font-family: var(--font-pretendard-bold);
    font-size: 1.25rem;
  }
  input[type='text'],
  input[type='password'] {
    padding: 0.5rem 3.25rem 0.5rem 1.25rem;
    height: 3rem;
    border-radius: 0.625rem;
    &:focus, &:focus-visible {
      outline-color: ${rootFixedColor.primaryColor};
    }
  }
  input[type='checkbox']{
    appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    background-color: #555555;
    border-radius: 0.25rem;
    &:checked {
      background-color: ${rootFixedColor.primaryColor};
    }
  }
  p {
    font-size: 12px;
  }
  .login-tab {
    display: flex;
    justify-content: center;
    align-items: center;
    // border-bottom: 1px solid #fff;
    text-align: center;
    background-color: #222222;
    div {
      flex: 1;
      padding: 1rem 0;
      text-align: center;
      border-radius: 1rem 1rem 0 0;
      cursor: pointer;
    }
    .active-tab {
      background-color: #333533;
    }
  }
`;
const MainWrap = styled.main`
  display: flex;
  flex-direction: column;
  width: 360px;
  border-radius: 1rem;
  gap: 1.5rem;
  background-color: ${prop => prop.theme.layer2BgColor};
`;
const InputWrapColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  position: relative;
  padding-bottom: 1rem;
  > div {
    position: relative;
    display: flex;
    gap: 0.5rem;
  }
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
  .info:hover {
    cursor: pointer;
    & ~ p {
      display: block;
      position: absolute;
      right: 0;
      width: 64%;
      top: -0.5rem;
      background-color: #555555;
      border-radius: 0.625rem;
      padding: 0.25rem 0.5rem;
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        margin: auto;
        left: -1.2rem;
        width: 1.2rem;
        height: 1.2rem;
        border: 0.625rem solid transparent;
        border-right-color: #555555;
      }
    }
  }
  .eye-on-off {
    position: absolute;
    right: 1rem;
    bottom: 1.75rem;
    svg {
      fill: #555555;
    }
    &:hover {
      cursor: pointer;
    }
  }
  p {
    color: #f9f9f9;
    display: none;
  }
`;
const InputWrapRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.625rem;
  padding-bottom: 1rem;
`;
const ButtonWrap = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  button {
    width: 50%;
    font-family: var(--font-pretendard-bold);
    font-size: 1rem;
    &:not(:disabled)&:hover {
      background-color: ${rootFixedColor.btnEditBgHoverColor};
    }
    &:disabled {
      background-color: ${rootFixedColor.secondaryColor};
    }
  }
`;
const DefaultBtn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background-color: ${rootFixedColor.primaryColor};
  color: ${rootFixedColor.rootTextColor};
`;
function AdminLogin() {
  const navigate = useNavigate();
  // 비밀번호 입력값 보이기/가리기
  const [eyeOnOff, setEyeOnOff] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>('Admin');
  function toggleOnOff() {
    setEyeOnOff(prev => !prev);
  }
  // 아이디/패스워드
  const [loginForm, setLoginForm] = useState({
    userId: "",
    userPassword: ""
  })
  const handleChangeValue = async (event: any) => {
    const { name, value } = event.target;
    if (name === "userId") {
      const lowercaseValue = value.toLowerCase();
      setLoginForm((prevData) => ({
        ...prevData,
        [name]: lowercaseValue,
      }));
    } else if (name === "userPassword") {
      const encPassword = encryptSHA256(value);
      setLoginForm((prevData) => ({
        ...prevData,
        [name]: encPassword,
      }));
    }
  };
  // 로그인 함수
  const [isValid, setIsValid] = useState(false);
  const handleLogin = async () => {
    const auth = authEncryptAes256(
      generateSecureRandomString(),
      loginForm.userId,
      loginForm.userPassword,
    );
    if (activeTab === 'Admin') {
      const result = await adminLogin(auth);
      if (result.code === 200) {
        const loginAuthCode: string[] = Array.isArray(result.data.auth_code) ? result.data.auth_code : []; // 배열로 변환
        if (result.data.auth_group === 'M') {
          alert('해당 ID는 대원 전용 ID 입니다.');
          return;
        } else if (loginAuthCode.length === 0) {
          alert('모든 권한이 없습니다.');
          return;
        } else if (loginAuthCode.includes('A')) {
          navigate('/admin/user/list');
        } else if (loginAuthCode.includes('C')) {
          navigate('/admin/contract/list');
        } else if (loginAuthCode.includes('D')) {
          navigate('/admin/notice/list');
        } else if (loginAuthCode.includes('E')) {
          navigate('/admin/mgmt/list');
        } else if (loginAuthCode.includes('G')) {
          navigate('/admin/after-service');
        }
        setCookieLoginData();
      } else {
        return alert(result.message);
      }
    } else if (activeTab === 'Agent') {
      const result = await agentLogin(auth);
      if (result.code === 200) {
        navigate('/agent/menu');
        setCookieLoginData();
      } else {
        return alert(result.message);
      }
    } else {
      alert('잘못된 접근입니다.');
    }
  }
  const setCookieLoginData = () => {
    // 로그인 성공 시 cookie에 아이디 저장
    const today = new Date();
    const expires = today.setDate(today.getDate() + 7);
    if (isRemember) setCookie('rememberAdminId', loginForm.userId, { maxAge: expires })
    else removeCookie('rememberAdminId');
  }
  const handleKeyDown = async (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && isValid) handleLogin();
  }
  // 아이디 기억하기
  const [isRemember, setIsRemember] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies(['rememberAdminId']);

  function handleRemember(e:React.ChangeEvent<HTMLInputElement>) {
    setIsRemember(e.target.checked);
  }
  // 쿠키에 저장되어 있으면 가져오기
  useEffect(() => {
    if (cookies.rememberAdminId !== undefined) {
      setLoginForm({ ...loginForm, userId: cookies.rememberAdminId });
      setIsRemember(true);
    }
  }, []);
  // 아이디/비밀번호가 다 입력됐을 때 로그인 버튼 활성화
  useEffect(() => {
    if (loginForm.userId && loginForm.userPassword) setIsValid(true);
    else setIsValid(false);
  },[loginForm]);
  
  const [darkAtom, setDarkAtom] = useRecoilState(isDarkAtom);
  useEffect(() => {
    setDarkAtom(true);
  }, [darkAtom]);
  
  // 인증 정보 검증
  useEffect(() => {
    if (window.location.protocol === 'http:' && !window.location.port) {
      const hostname = window.location.hostname;
      const isIP = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);
    
      if (!isIP) { // 도메인으로 접속한 경우에만 HTTPS 리디렉션
        const httpsUrl = `https://${hostname}${window.location.pathname}${window.location.search}`;
        window.location.replace(httpsUrl);
      }
    }
  }, []);
  return (
    <>
      <MainContainer>
        {/* 로고 */}
        <figure>
          <img src="/image/FOCUSAI_Red_White.png" alt="logo" style={{ height: "50px" }}/>
        </figure>
        <MainWrap>
          <div className={`login-tab`}>
            <div className={`${activeTab === 'Admin' ? 'active-tab' : ''} title`} onClick={() => setActiveTab("Admin")}>관리자</div>
            <div className={`${activeTab === 'Agent' ? 'active-tab' : ''} title`} onClick={() => setActiveTab("Agent")}>매니저</div>
          </div>
          <div style={{ padding: "0 2rem 2rem" }}>
            <InputWrapColumn>
              <label htmlFor="loginId" className="title">ID</label>
              <input type='text' name="userId" id="loginId" value={loginForm.userId} onChange={handleChangeValue} placeholder="ID 입력" onKeyUp={handleKeyDown}/>
            </InputWrapColumn>
            <InputWrapColumn>
              <div>
                <label htmlFor="loginPw" className="title">PASSWORD</label>
                {/* <span className="info"><FcAbout /></span>
                <p>PASSWORD INFO</p> */}
              </div>
              <input type={!eyeOnOff ? 'password' : 'text'} name="userPassword" id="loginPw" onChange={handleChangeValue} placeholder="PASSWORD 입력" onKeyUp={handleKeyDown} />
              <div className='eye-on-off' onClick={toggleOnOff}>
                {!eyeOnOff ? <BsEyeSlash /> : <BsEye />}
              </div>
            </InputWrapColumn>
            <InputWrapRow>
              <input type='checkbox' id="saveId" checked={isRemember} onChange={(e) => handleRemember(e)}/>
              <label htmlFor="saveId">ID 저장</label>
            </InputWrapRow>
            <ButtonWrap>
              <DefaultBtn type="button" onClick={handleLogin} disabled={!isValid}>LOG IN</DefaultBtn>
            </ButtonWrap>
          </div>
        </MainWrap>
        <p>© 2025 FOCUS AI. ALL RIGHT RESERVED.</p>
      </MainContainer>
    </>
  );
}

export default AdminLogin;